import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ReportsData } from "@/hooks/useReportsData";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer } from "recharts";
import { format } from "date-fns";
import { BookCheck, Target, Award, TrendingUp } from "lucide-react";

interface ExamsAnalyticsProps {
  data: ReportsData;
  compact?: boolean;
}

const QUESTION_TYPE_LABELS: Record<string, string> = {
  "multiple-choice": "Multiple Choice",
  "true-false": "True/False",
  "short-answer": "Short Answer",
  "fill-blank": "Fill in the Blank",
};

export const ExamsAnalytics: React.FC<ExamsAnalyticsProps> = ({ data, compact = false }) => {
  const totalExams = data.examAttempts.length;
  const avgScore =
    totalExams > 0
      ? Math.round(
          data.examAttempts.reduce((sum, a) => sum + (a.score || 0), 0) /
            totalExams
        )
      : 0;

  const bestScore =
    totalExams > 0
      ? Math.max(...data.examAttempts.map((a) => a.score || 0))
      : 0;

  // Question type performance data
  const questionTypeData = data.examQuestionTypeStats.map((stat) => ({
    name: QUESTION_TYPE_LABELS[stat.question_type] || stat.question_type,
    accuracy: stat.accuracy,
    fill: `hsl(var(--chart-${(data.examQuestionTypeStats.indexOf(stat) % 5) + 1}))`,
  }));

  const chartConfig = {
    accuracy: { label: "Accuracy", color: "hsl(var(--chart-1))" },
  };

  if (totalExams === 0) {
    return (
      <Card className="border-border bg-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <BookCheck className="h-5 w-5" />
            Exams
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex h-48 items-center justify-center text-center">
            <div>
              <BookCheck className="mx-auto mb-3 h-12 w-12 text-muted-foreground/50" />
              <p className="text-muted-foreground">
                Take an exam to track your performance and identify areas for
                improvement.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (compact) {
    return (
      <Card className="border-border bg-card">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-lg">
            <BookCheck className="h-5 w-5" />
            Exams
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Exams Taken</span>
              <span className="font-medium text-foreground">{totalExams}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Average Score</span>
              <span className="font-medium text-foreground">{avgScore}%</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Best Score</span>
              <span className="font-medium text-foreground">{bestScore}%</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Accuracy</span>
              <span className="font-medium text-foreground">{data.overallExamAccuracy}%</span>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card className="border-border bg-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-primary/10 p-2">
                <BookCheck className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Exams Taken</p>
                <p className="text-xl font-semibold">{totalExams}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-border bg-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-info/10 p-2">
                <Target className="h-5 w-5 text-info" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Average Score</p>
                <p className="text-xl font-semibold">{avgScore}%</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-border bg-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-correct/10 p-2">
                <Award className="h-5 w-5 text-correct" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Best Score</p>
                <p className="text-xl font-semibold">{bestScore}%</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-border bg-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-premium/10 p-2">
                <TrendingUp className="h-5 w-5 text-premium" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Overall Accuracy</p>
                <p className="text-xl font-semibold">{data.overallExamAccuracy}%</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Question Type Performance */}
        <Card className="border-border bg-card">
          <CardHeader>
            <CardTitle className="text-lg">Performance by Question Type</CardTitle>
          </CardHeader>
          <CardContent>
            {questionTypeData.length > 0 ? (
              <ChartContainer config={chartConfig} className="h-48 w-full">
                <BarChart data={questionTypeData} layout="vertical">
                  <XAxis
                    type="number"
                    domain={[0, 100]}
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis
                    dataKey="name"
                    type="category"
                    tickLine={false}
                    axisLine={false}
                    tick={{ fontSize: 12 }}
                    width={100}
                  />
                  <ChartTooltip content={<ChartTooltipContent />} />
                  <Bar dataKey="accuracy" radius={4} />
                </BarChart>
              </ChartContainer>
            ) : (
              <div className="flex h-48 items-center justify-center">
                <p className="text-sm text-muted-foreground">No data yet</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Question Type Stats Grid */}
        <Card className="border-border bg-card">
          <CardHeader>
            <CardTitle className="text-lg">Accuracy Breakdown</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3 sm:grid-cols-2">
              {data.examQuestionTypeStats.map((stat) => (
                <div
                  key={stat.question_type}
                  className="rounded-lg border border-border bg-muted/30 p-4"
                >
                  <p className="text-sm text-muted-foreground">
                    {QUESTION_TYPE_LABELS[stat.question_type] || stat.question_type}
                  </p>
                  <p className="mt-1 text-2xl font-semibold">{stat.accuracy}%</p>
                  <p className="text-xs text-muted-foreground">
                    {stat.correct}/{stat.total} correct
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Exams Table */}
      <Card className="border-border bg-card">
        <CardHeader>
          <CardTitle className="text-lg">Recent Exams</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border text-left">
                  <th className="pb-3 text-sm font-medium text-muted-foreground">
                    Exam
                  </th>
                  <th className="pb-3 text-sm font-medium text-muted-foreground">
                    Date
                  </th>
                  <th className="pb-3 text-sm font-medium text-muted-foreground">
                    Score
                  </th>
                  <th className="pb-3 text-sm font-medium text-muted-foreground">
                    Questions
                  </th>
                  <th className="pb-3 text-sm font-medium text-muted-foreground">
                    Performance
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.examAttempts.slice(0, 10).map((exam) => {
                  const scorePercent = exam.score || 0;
                  return (
                    <tr key={exam.id} className="border-b border-border/50">
                      <td className="py-3 font-medium">{exam.title}</td>
                      <td className="py-3 text-muted-foreground">
                        {exam.submitted_at
                          ? format(new Date(exam.submitted_at), "MMM d, yyyy")
                          : "-"}
                      </td>
                      <td className="py-3">
                        <span
                          className={`font-medium ${
                            scorePercent >= 70
                              ? "text-correct"
                              : scorePercent >= 50
                              ? "text-orange"
                              : "text-incorrect"
                          }`}
                        >
                          {scorePercent}%
                        </span>
                      </td>
                      <td className="py-3 text-muted-foreground">
                        {exam.total_correct}/{exam.total_questions}
                      </td>
                      <td className="py-3">
                        <div className="flex items-center gap-2">
                          <div className="h-2 w-24 overflow-hidden rounded-full bg-muted">
                            <div
                              className={`h-full rounded-full ${
                                scorePercent >= 70
                                  ? "bg-correct"
                                  : scorePercent >= 50
                                  ? "bg-orange"
                                  : "bg-incorrect"
                              }`}
                              style={{ width: `${scorePercent}%` }}
                            />
                          </div>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
